import { JSX } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LazyRoute } from '@bg-components/LazyRoute';
import { ResultsReport } from '@bg-components/Reports/pages/ResultsReport';
import { VideoArchive, VideoArchiveSkeleton } from '@bg-components/Reports/pages/VideoArchive';
import { ErrorPage } from '@bg-components/Pages/Error';
import { LevelUpMessage } from '@bg-components/LevelUpMessage';
import { ReportSkeleton } from '@bg-components/Reports/components/ReportSkeleton/ReportSkeleton';
import { GeoBlockedPopup } from '@iframe-shell/components/GeoBlockedPopup';
import { TopNavigation } from '@iframe-shell/components/TopNavigation';
import {
    partnerSettingsStore,
    t,
    usePageViewTracking,
    routeUrlGenerator,
    useWebAPIIntegration,
    envConfig,
} from '@bg-shared';
import { LobbySkeleton } from '../Lobby/LobbySkeleton';
import { Game } from '../Game';
import { Lobby } from '../Lobby';
import { useRefreshIframe } from './hooks/useRefreshIframe';
import { RnD } from '../RnD';
import { useHeight } from './hooks/useHeight';

export const App = (): JSX.Element => {
    const location = useLocation();
    const { partnerSettings } = partnerSettingsStore;
    const currentPageIsLobby = location.pathname.includes(routeUrlGenerator.routes.LOBBY);

    useRefreshIframe();
    usePageViewTracking();
    useWebAPIIntegration();
    useHeight();

    if (!partnerSettingsStore.availableGamesIds.length) {
        return (
            <LazyRoute>
                <ErrorPage message={t.string('not_available')} />
            </LazyRoute>
        );
    }

    return (
        <>
            {partnerSettings.isGeoBlocked && <GeoBlockedPopup />}
            {!currentPageIsLobby && <TopNavigation />}
            {partnerSettings.isGamificationEnabled && <LevelUpMessage />}
            <Routes>
                <Route
                    path={routeUrlGenerator.routes.LOBBY}
                    element={
                        <LazyRoute Fallback={<LobbySkeleton />}>
                            <Lobby />
                        </LazyRoute>
                    }
                />
                <Route>
                    <Route path={`${routeUrlGenerator.routes.GAME}/:gameId/*`} element={<Game />} />
                    {(envConfig.isDevEnv || envConfig.isStagingEnv) && (
                        <Route
                            path={`${routeUrlGenerator.routes.RND}/:game`}
                            element={
                                <LazyRoute>
                                    <RnD />
                                </LazyRoute>
                            }
                        />
                    )}
                    <Route path={`${routeUrlGenerator.routes.RESULTS}/:date/:page/:gameId`}>
                        <Route
                            path=":run"
                            element={
                                <LazyRoute Fallback={<ReportSkeleton filtersCount={3} />}>
                                    <ResultsReport />
                                </LazyRoute>
                            }
                        />
                        <Route
                            path=""
                            element={
                                <LazyRoute Fallback={<ReportSkeleton filtersCount={3} />}>
                                    <ResultsReport />
                                </LazyRoute>
                            }
                        />
                    </Route>
                    <Route
                        path={`${routeUrlGenerator.routes.VIDEO}/:run`}
                        element={
                            <LazyRoute Fallback={<VideoArchiveSkeleton />}>
                                <VideoArchive />
                            </LazyRoute>
                        }
                    />

                    {['/', '*'].map((path) => {
                        return (
                            <Route
                                path={path}
                                key={path}
                                element={<Navigate to={routeUrlGenerator.routes.LOBBY} replace />}
                            />
                        );
                    })}
                </Route>
            </Routes>
        </>
    );
};
